import { DS_SHAPE_SPACING } from '@calm/evexia-design-tokens';
import isSSR from '@calm/web-common/dist/isSSR';
import { BANNER_V2_MAX_WIDTH } from '@rhg/components/banner-card/types';
import { Carousel } from '@rhg/components/carousel';
import { fullPagePaddingPx } from '@rhg/components/full-page-container';
import { useCurrentBreakpointName } from '@rhg/hooks/use-current-breakpoint-name';
import { useIsMobile } from '@rhg/hooks/use-is-mobile';
import { useWindowDimensions } from '@rhg/hooks/use-window-dimensions';
import { Box } from '@rhg/libs/material';
import { getValueByBreakpoint, sxAppend } from '@rhg/libs/material/sx';
import { Children, useCallback, useMemo, useRef, useState } from 'react';
import { CarouselProps } from '../types';
import { defaultCarouselCardSpacing } from '../utils';

const styleCarousel = { justifyContent: 'flex-start' };

export interface FullscreenCarouselProps extends CarouselProps {
    /** Override the default itemWidth calculations and use a more dynamic approach */
    fullWidthItem?: boolean;
}

const styleButtonContainer = {
    width: 'calc(100dvw - 24px)',
    left: '20px',
};

const styleMobileButtonContainer = {
    width: {
        xs: '100dvw',
        md: 'calc(100% + 40px + 30px)',
    },
    left: {
        xs: '-25px',
        md: '-40px',
    },
    top: {
        xs: '46px',
        md: 'unset',
    },
};
const styleListBefore = {
    ':before': {
        content: '" "',
    },
};
/**
 * A carousel that automatically resizes so that it extends to both edges of
 * the screen.
 */
export function FullscreenCarousel({
    sx,
    wrapperSx,
    children,
    listSx,
    fullWidthItem,
    ...props
}: FullscreenCarouselProps) {
    const [width, setWidth] = useState<number>();
    const containerRef = useRef<HTMLDivElement>(null);
    const isMobile = useIsMobile();
    const currentBreakpoint = useCurrentBreakpointName();

    const setWindowWidth = useCallback((windowWidth?: number) => {
        if (!isSSR() && windowWidth) {
            const leftOffset =
                containerRef.current?.getBoundingClientRect()?.left ?? 0;
            setWidth(windowWidth - leftOffset);
        }
    }, []);

    useWindowDimensions(({ clientWidth: windowWidth }) => {
        setWindowWidth(windowWidth);
    });

    const desktopGap = defaultCarouselCardSpacing; // This spacing is constant for now. We'll need to change this if we want to support other spacings
    const leadingColumnWidth = !isMobile
        ? DS_SHAPE_SPACING.SIDE_LARGE - desktopGap
        : 0;

    const containerPaddingValue = useMemo(
        () => getValueByBreakpoint(fullPagePaddingPx, currentBreakpoint) ?? 0,
        [currentBreakpoint]
    );
    const maxWidth = useMemo(() => {
        const usedWith = width ?? window?.innerWidth;
        return usedWith > BANNER_V2_MAX_WIDTH
            ? BANNER_V2_MAX_WIDTH
            : usedWith - containerPaddingValue * 2;
    }, [containerPaddingValue, width]);

    // Children.count() counts the number of slots that children has, regardless of false, null, or undefined
    // Children.toArray() converts children received to a structure containing only elements JSX can render
    const childrenLength = Children.toArray(children).length;

    return (
        <Box
            ref={containerRef}
            sx={sxAppend(wrapperSx, {
                width: fullWidthItem && width ? '100%' : width,
            })}
        >
            <Carousel
                showItemPreview
                toggleButtonsOnHover={!fullWidthItem}
                buttonContainerSx={
                    fullWidthItem
                        ? styleMobileButtonContainer
                        : styleButtonContainer
                }
                {...props}
                listSx={{
                    gridTemplateColumns: fullWidthItem
                        ? `repeat(${childrenLength}, 100%)`
                        : `${leadingColumnWidth}px repeat(${childrenLength}, minmax(${props.itemWidth}px, 1fr)) ${leadingColumnWidth}px`,
                    ...(fullWidthItem ? {} : styleListBefore),
                    ...listSx,
                }}
                sx={sxAppend(styleCarousel, sx)}
                itemWidth={fullWidthItem ? maxWidth : props.itemWidth}
                wrapperSx={{
                    position: 'relative',
                    maxWidth: maxWidth ?? 'initial',
                }}
            >
                {children}
            </Carousel>
        </Box>
    );
}
